import React, { useEffect, useState } from "react";

import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Divider, MenuItem, Select } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";

import { Cross, RafterDownIcon, SearchIcon } from "@/assets/icons";
import { SearchContext, SearchResult } from "@/shared/apis/searchApi";

import ResultsList from "./components/ResultsList";
import { StyedDialog, StyledQuickSearchButtonContainer } from "./styled";
import { getNextEntry, getPreviousEntry } from "./utils/array";
import { getSearchItemId } from "./utils/searchItemId";
import useSearch from "./utils/useSearch";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const QuickSearchMobile = () => {
    const navigate = useNavigate();
    const [currentFocus, setCurrentFocus] = useState<SearchResult>(null);

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const { t } = useTranslation();

    const handleClose = () => {
        setIsOpen(false);
    };

    const searchPopoverId = "search-popover-id";

    const { isBusy, searchContext, setSearchContext, message, results, setSearchWord, filters } = useSearch({
        isMobile: true,
    });

    const selectPrevious = () => {
        setCurrentFocus(getPreviousEntry(results, currentFocus));
    };

    const selectNext = () => {
        setCurrentFocus(getNextEntry(results, currentFocus));
    };

    const openEntry = (event, item?: SearchResult) => {
        if (item) setCurrentFocus(item);
        if (currentFocus?.isAccessible) {
            if (location.pathname !== currentFocus.url) {
                navigate(currentFocus.url, { replace: true, state: currentFocus.state });
            }
            handleClose();
            event?.preventDefault();
        }
    };

    useEffect(() => {
        if (results.length > 0) {
            setCurrentFocus(results[0]);
        }
    }, [results]);

    return (
        <>
            <StyledQuickSearchButtonContainer aria-owns={searchPopoverId}>
                <Button
                    disableElevation
                    color="secondary"
                    size="small"
                    aria-label={t("Common.Search")}
                    id="quick_search-icon-button"
                    data-testid="quick_search-icon-button"
                    onClick={() => setIsOpen(true)}
                >
                    <SearchIcon />
                </Button>
            </StyledQuickSearchButtonContainer>

            <StyedDialog
                aria-labelledby="quick_search-icon-button"
                TransitionComponent={Transition}
                fullScreen
                id={searchPopoverId}
                open={isOpen}
            >
                <header>
                    <div className="search-field-container">
                        <InputBase
                            type="search"
                            className={clsx(isOpen && "popup-visible")}
                            classes={{ root: "input-container", input: "input" }}
                            onChange={(e) => setSearchWord(e.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "ArrowUp" && isOpen && results.length > 0) {
                                    event.preventDefault();
                                    selectPrevious();
                                } else if (event.key === "ArrowDown" && isOpen && results.length > 0) {
                                    event.preventDefault();
                                    selectNext();
                                } else if (event.key === "Enter" && isOpen && results.length > 0) {
                                    openEntry(event);
                                } else if (event.key === "Escape" && isOpen) {
                                    handleClose();
                                }
                            }}
                            data-testid="quick_search-input-search"
                            placeholder={t("Common.Search")}
                            inputProps={{
                                "aria-controls": searchPopoverId,
                                ...(currentFocus != null && { "aria-activedescendant": getSearchItemId(currentFocus) }),
                                autoCapitalize: "none",
                                spellCheck: "false",
                                autoComplete: "no",
                                role: "searchbox",
                                "aria-label": t("Common.Search"),
                                "aria-busy": isBusy,
                                "data-testid": "quick_search-input-",
                            }}
                        />

                        <Button
                            title={t("User.PersonalDetails.Submit")}
                            disableElevation
                            color="secondary"
                            size="small"
                            data-testid="quick_search-icon-button"
                        >
                            <SearchIcon />
                        </Button>

                        <Button
                            title={t("Common.Close")}
                            disableElevation
                            color="primary"
                            size="small"
                            data-testid="quick_search-close-button"
                            onClick={() => handleClose()}
                        >
                            <Cross />
                        </Button>
                    </div>
                    {filters.length > 2 && (
                        <>
                            <Divider />
                            <Select
                                fullWidth
                                className="select-root"
                                classes={{ select: "select", icon: "select-icon" }}
                                id="quick_search-select-category"
                                value={searchContext}
                                label={t("Common.Filter")}
                                IconComponent={RafterDownIcon}
                                onChange={(event) => setSearchContext(event.target.value as SearchContext)}
                            >
                                {filters.map((entry) => (
                                    <MenuItem key={`quick_search-select-category-${entry.id}`} value={entry.id}>
                                        {t(entry.label)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </>
                    )}
                </header>
                {results.length > 0 && (
                    <ResultsList
                        labelledby="quick_search-icon-button"
                        focus={currentFocus}
                        openEntry={openEntry}
                        setFocus={setCurrentFocus}
                        results={results}
                    />
                )}
                <footer>{message !== null && <p role="status">{message}</p>}</footer>
            </StyedDialog>
        </>
    );
};

export default QuickSearchMobile;
